import React, { useState } from "react";
import { Select, Input, Button, DatePicker } from "antd";
import {
  EMPLOYMENT_TYPE_OPTIONS,
  JOB_STATUSES,
} from "../../constants/recruitment.constants";
import type { JobFilters as Filters } from "../../../../core/data/redux/recruitment/types";
import dayjs, { Dayjs } from "dayjs";
const { RangePicker } = DatePicker;

interface JobFiltersProps {
  filters: Filters;
  onFilterChange: (partial: Partial<Filters>) => void;
  viewMode: "grid" | "list";
  onViewModeChange: (mode: "grid" | "list") => void;
  totalCount?: number;
  isLoading?: boolean;
  departmentOptions: { value: number; label: string }[];
}

const statusOptions = JOB_STATUSES.map((s) => ({ value: s, label: s }));
const typeOptions = EMPLOYMENT_TYPE_OPTIONS.map((o: any) => ({ value: o.value, label: o.label }));

const JobFilters: React.FC<JobFiltersProps> = ({
  filters,
  onFilterChange,
  viewMode,
  onViewModeChange,
  totalCount = 0,
  isLoading = false,
  departmentOptions,
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const [localDateRange, setLocalDateRange] = useState<[Dayjs, Dayjs] | null>(
    (filters as any).date_from && (filters as any).date_to
      ? [dayjs((filters as any).date_from), dayjs((filters as any).date_to)]
      : null,
  );

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.department_id) count++;
    if (filters.employment_type) count++;
    if (filters.status) count++;
    if (filters.search) count++;
    if ((filters as any).date_from || (filters as any).date_to) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  const handleReset = () => {
    setLocalSearch("");
    setLocalDateRange(null);
    onFilterChange({
      search: "",
      department_id: null,
      employment_type: null,
      status: null,
      date_from: null,
      date_to: null,
      page: 1,
    } as any);
  };

  const handleSearchClick = () => {
    onFilterChange({ search: localSearch, page: 1 });
  };

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
  ) => {
    if (dates && dates[0] && dates[1]) {
      setLocalDateRange([dates[0], dates[1]]);
      onFilterChange({
        date_from: dates[0].format("YYYY-MM-DD"),
        date_to: dates[1].format("YYYY-MM-DD"),
        page: 1,
      } as any);
    } else {
      setLocalDateRange(null);
      onFilterChange({ date_from: null, date_to: null, page: 1 } as any);
    }
  };

  const filterOption = (input: string, option?: { label: string; value: any }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <div className="card">
      <div className="card-header border-0 pb-0">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1 d-flex align-items-center">
              <i className="ti ti-briefcase text-info fs-32 me-2" />
              Jobs List
            </h4>
            <p className="text-muted mb-0">Manage all job postings</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-icon btn-sm ${viewMode === "grid" ? "btn-info" : "btn-light"}`}
                onClick={() => onViewModeChange("grid")}
              >
                <i className="ti ti-layout-grid" />
              </button>
              <button
                type="button"
                className={`btn btn-icon btn-sm ${viewMode === "list" ? "btn-info" : "btn-light"}`}
                onClick={() => onViewModeChange("list")}
              >
                <i className="ti ti-list" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
        <div className="d-flex gap-2">
          <div className="d-flex gap-2 mb-2 me-2">
            <Input
              placeholder="Search jobs..."
              value={localSearch}
              onChange={(e) => {
                const value = e.target.value;
                setLocalSearch(value);
                if (value === "") {
                  onFilterChange({ search: "", page: 1 });
                }
              }}
              onPressEnter={handleSearchClick}
              style={{ width: 210, minHeight: 38 }}
              allowClear
            />
            <Button type="primary" onClick={handleSearchClick} style={{ minHeight: 38 }}>
              <i className="ti ti-search me-1" />
            </Button>
          </div>
        </div>

        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 160, minHeight: 38 }}
              placeholder="Department"
              value={filters.department_id || undefined}
              allowClear
              onChange={(value) => onFilterChange({ department_id: value || null, page: 1 })}
              options={departmentOptions}
            />
          </div>
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 150, minHeight: 38 }}
              placeholder="Job Type"
              value={filters.employment_type || undefined}
              allowClear
              onChange={(value) => onFilterChange({ employment_type: value || null, page: 1 })}
              options={typeOptions}
            />
          </div>
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 140, minHeight: 38 }}
              placeholder="Status"
              value={filters.status || undefined}
              allowClear
              onChange={(value) => onFilterChange({ status: value || null, page: 1 })}
              options={statusOptions}
            />
          </div>
          <div className="me-2">
            <RangePicker
              size="small"
              value={localDateRange}
              onChange={(dates) =>
                handleDateRangeChange(dates as [Dayjs | null, Dayjs | null] | null)
              }
              allowClear
              disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
              placeholder={["From date", "To date"]}
              style={{ borderRadius: 8 }}
              format="DD MMM YYYY"
            />
          </div>
          <div>
            <button
              type="button"
              className={`btn btn-md ${activeFiltersCount > 0 ? "btn-warning" : "btn-outline-danger"} mb-2 position-relative py-2`}
              onClick={handleReset}
            >
              <i className="ti ti-refresh me-1" />
              Reset
              {activeFiltersCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "10px" }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobFilters;